import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import UserSessionForm from "./UserSessionForm";
import {
    fetchUserSession,
    updateUserSession,
    clearCurrentUserSession,
} from "../../store/slices/userSessionSlice";

const EditUserSession = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const { currentUserSession, isLoading, error } = useSelector(
        (state) => state.userSessions
    );

    const [formData, setFormData] = useState({
        sessionCategoryMasterId: "",
        patientId: "",
        caseId: "",
        sessionMasterId: "",
        subSessionMasterId: "",
        summaryOfSession: "",
        date: "",
        time: "",
        satisfaction: "",
        satisfactionFeedback: "",
        patientIdReadOnly: true,
    });

    const [errors, setErrors] = useState({});

    // Fetch the session details on mount
    useEffect(() => {
        if (id) {
            dispatch(fetchUserSession(id));
        }
    }, [dispatch, id]);

    // Cleanup session details on unmount
    useEffect(() => {
        return () => {
            dispatch(clearCurrentUserSession());
        };
    }, [dispatch]);

    // Populate form data once fetched
    useEffect(() => {
        if (currentUserSession && currentUserSession.id === parseInt(id)) {
            setFormData({
                sessionCategoryMasterId: currentUserSession.session_category_master_id || "",
                patientId: currentUserSession.patient_id || "",
                caseId: currentUserSession.case_id || "",
                sessionMasterId: currentUserSession.session_master_id || "",
                subSessionMasterId: currentUserSession.sub_session_master_id || "",
                summaryOfSession: currentUserSession.summary_of_session || "",
                date: currentUserSession.date ? currentUserSession.date.split("T")[0] : "",
                time: currentUserSession.time ? currentUserSession.time.substring(0, 5) : "",
                satisfaction: currentUserSession.satisfaction || "",
                satisfactionFeedback: currentUserSession.satisfaction_feedback || "",
                patientIdReadOnly: true,
            });
        }
    }, [currentUserSession, id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.patientId) newErrors.patientId = "Patient is required";
        if (!formData.sessionCategoryMasterId) newErrors.sessionCategoryMasterId = "Session Category is required";
        if (!formData.sessionMasterId) newErrors.sessionMasterId = "Session Type is required";
        if (!formData.subSessionMasterId) newErrors.subSessionMasterId = "Sub Session Type is required";
        if (!formData.date) newErrors.date = "Date is required";
        if (!formData.time) newErrors.time = "Time is required";
        if (!formData.satisfaction) newErrors.satisfaction = "Satisfaction rating is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const payload = {
                session_category_master_id: formData.sessionCategoryMasterId,
                session_master_id: formData.sessionMasterId,
                sub_session_master_id: formData.subSessionMasterId,
                patient_id: formData.patientId,
                case_id: formData.caseId || null,
                summary_of_session: formData.summaryOfSession,
                date: formData.date,
                time: formData.time,
                satisfaction: formData.satisfaction,
                satisfaction_feedback: formData.satisfactionFeedback,
                status: "active",
            };

            await dispatch(updateUserSession({ id, formData: payload })).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Session record updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate("/sessions");
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: typeof err === "object" ? Object.values(err).flat().join(" ") : (err.message || "Failed to update session record"),
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="alert alert-danger m-4">{error}</div>
                        ) : (
                            <>
                                {Object.keys(errors).length > 0 && (
                                    <div className="alert alert-danger">
                                        {Object.values(errors).map((err, i) => (
                                            <div key={i}>{err}</div>
                                        ))}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <UserSessionForm
                                        formData={formData}
                                        handleChange={handleChange}
                                    />

                                    <div className="row mt-3 px-4">
                                        <div className="col-md-2 ms-auto">
                                            <button type="submit" className="btn btn-primary w-100">
                                                <i className="ri-save-3-line me-1"></i> Update
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditUserSession;
